/**
 * QueueTabs - Tab bar for switching between record queues
 *
 * Three tabs: Eligible Complaints, Vulnerable Customers, All Records.
 * Reads `?queue=` URL param for pre-selection when arriving from Overview "View all".
 *
 * Atomic level: Molecule
 */

import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export type QueueType = 'complaints' | 'vulnerability' | 'all';

interface QueueTab {
  id: QueueType;
  label: string;
  count: number;
}

interface QueueTabsProps {
  eligibleComplaintsCount: number;
  vulnerableCustomersCount: number;
  allRecordsCount: number;
  activeQueue: QueueType;
  onTabChange: (queue: QueueType) => void;
  className?: string;
}

const QueueTabs: React.FC<QueueTabsProps> = ({
  eligibleComplaintsCount,
  vulnerableCustomersCount,
  allRecordsCount,
  activeQueue,
  onTabChange,
  className = '',
}) => {
  const [searchParams] = useSearchParams();

  // Pre-select queue from URL param on mount
  useEffect(() => {
    const queueParam = searchParams.get('queue') as QueueType | null;
    if (queueParam && ['complaints', 'vulnerability', 'all'].includes(queueParam)) {
      onTabChange(queueParam);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs: QueueTab[] = [
    { id: 'complaints', label: 'Eligible Complaints', count: eligibleComplaintsCount },
    { id: 'vulnerability', label: 'Vulnerable Customers', count: vulnerableCustomersCount },
    { id: 'all', label: 'All Records', count: allRecordsCount },
  ];

  return (
    <div className={`flex gap-0 border-b border-border ${className}`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`
            font-sans text-[13px] font-semibold px-4 py-2.5 border-b-2 transition-all cursor-pointer
            ${
              activeQueue === tab.id
                ? 'text-[#4342F5] border-[#4342F5]'
                : 'text-muted-foreground border-transparent hover:text-foreground'
            }
          `}
        >
          {tab.label}{' '}
          <span className="font-bold ml-0.5">{tab.count.toLocaleString()}</span>
        </button>
      ))}
    </div>
  );
};

export { QueueTabs };
export type { QueueTabsProps };
